import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentListResponse,
  AgentExecutionRequest,
  AgentExecutionResponse,
  AgentMetrics,
  IntegrationTool,
} from '@/types';
import { apiClient } from './api';

export const agentsService = {
  async getAgents(params?: {
    workspace_id: string;
    page?: number;
    per_page?: number;
    search?: string;
    is_active?: boolean;
  }): Promise<AgentListResponse> {
    try {
      const {
        workspace_id,
        page = 1,
        per_page = 20,
        search,
        is_active
      } = params || {};

      if (!workspace_id) {
        throw new Error('Workspace ID is required');
      }

      const queryParams = new URLSearchParams({
        workspace_id,
        page: page.toString(),
        per_page: per_page.toString(),
      });

      if (search) {
        queryParams.append('search', search);
      }

      if (is_active !== undefined) {
        queryParams.append('is_active', is_active.toString());
      }

      const response = await apiClient.get<AgentListResponse>(`/agents?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  },

  async getAgent(id: number): Promise<Agent> {
    try {
      const response = await apiClient.get<Agent>(`/agents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent:', error);
      throw error;
    }
  },

  async createAgent(data: CreateAgentRequest): Promise<Agent> {
    try {
      const response = await apiClient.post<Agent>('/agents', data);
      return response.data;
    } catch (error) {
      console.error('Error creating agent:', error);
      throw error;
    }
  },

  async updateAgent(id: number, data: UpdateAgentRequest): Promise<Agent> {
    try {
      const response = await apiClient.put<Agent>(`/agents/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating agent:', error);
      throw error;
    }
  },

  async deleteAgent(id: number): Promise<void> {
    try {
      await apiClient.delete(`/agents/${id}`);
    } catch (error) {
      console.error('Error deleting agent:', error);
      throw error;
    }
  },

  async executeAgent(id: number, executionData: AgentExecutionRequest): Promise<AgentExecutionResponse> {
    try {
      const response = await apiClient.post<AgentExecutionResponse>(`/agents/${id}/execute`, executionData);
      return response.data;
    } catch (error) {
      console.error('Error executing agent:', error);
      throw error;
    }
  },

  async duplicateAgent(id: number): Promise<Agent> {
    try {
      const response = await apiClient.post<Agent>(`/agents/${id}/duplicate`);
      return response.data;
    } catch (error) {
      console.error('Error duplicating agent:', error);
      throw error;
    }
  },

  async getAgentMetrics(workspace_id: string, limit: number = 10): Promise<AgentMetrics[]> {
    try {
      const response = await apiClient.get<AgentMetrics[]>(`/monitoring/workspace/${workspace_id}/agents?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching agent metrics:', error);
      throw error;
    }
  },

  async getAvailableTools(): Promise<IntegrationTool[]> {
    try {
      const response = await apiClient.get<{tools_by_type: Record<string, IntegrationTool[]>, total_tools: number}>('/integrations/tools');
      
      // Flatten the tools from all categories into a single array
      const allTools: IntegrationTool[] = [];
      Object.values(response.data.tools_by_type).forEach(tools => {
        allTools.push(...tools);
      });
      
      return allTools;
    } catch (error) {
      console.error('Error fetching available tools:', error);
      // Return fallback list of known tools
      return [
        { name: 'search_confluence_pages', description: 'Search Confluence pages', parameters: {}, integration_type: 'confluence' },
        { name: 'get_confluence_page', description: 'Get Confluence page content', parameters: {}, integration_type: 'confluence' },
        { name: 'create_product_requirements_document', description: 'Create product requirements document', parameters: {}, integration_type: 'confluence' },
        { name: 'create_technical_design_document', description: 'Create technical design document', parameters: {}, integration_type: 'confluence' },
        { name: 'get_jira_task', description: 'Get Jira task details', parameters: {}, integration_type: 'jira' },
        { name: 'update_jira_task_status', description: 'Update Jira task status', parameters: {}, integration_type: 'jira' },
        { name: 'get_github_repository', description: 'Get GitHub repository information', parameters: {}, integration_type: 'github' },
        { name: 'get_github_repository_content', description: 'Get GitHub repository content', parameters: {}, integration_type: 'github' },
        { name: 'create_implementation_branch', description: 'Create implementation branch', parameters: {}, integration_type: 'github' },
        { name: 'commit_implementation_code', description: 'Commit implementation code', parameters: {}, integration_type: 'github' },
        { name: 'create_implementation_pull_request', description: 'Create implementation pull request', parameters: {}, integration_type: 'github' },
      ];
    }
  },

  async getToolsByCategory(category?: string): Promise<Record<string, IntegrationTool[]>> {
    try {
      const response = await apiClient.get<{tools_by_type: Record<string, IntegrationTool[]>, total_tools: number}>('/integrations/tools');
      
      if (category) {
        return { [category]: response.data.tools_by_type[category] || [] };
      }
      
      return response.data.tools_by_type;
    } catch (error) {
      console.error('Error fetching tools by category:', error);
      // Return fallback categorized tools
      return {
        confluence: [
          { name: 'search_confluence_pages', description: 'Search Confluence pages', parameters: {}, integration_type: 'confluence' },
          { name: 'get_confluence_page', description: 'Get Confluence page content', parameters: {}, integration_type: 'confluence' },
          { name: 'create_product_requirements_document', description: 'Create product requirements document', parameters: {}, integration_type: 'confluence' },
          { name: 'create_technical_design_document', description: 'Create technical design document', parameters: {}, integration_type: 'confluence' },
        ],
        jira: [
          { name: 'get_jira_task', description: 'Get Jira task details', parameters: {}, integration_type: 'jira' },
          { name: 'update_jira_task_status', description: 'Update Jira task status', parameters: {}, integration_type: 'jira' },
        ],
        github: [
          { name: 'get_github_repository', description: 'Get GitHub repository information', parameters: {}, integration_type: 'github' },
          { name: 'get_github_repository_content', description: 'Get GitHub repository content', parameters: {}, integration_type: 'github' },
          { name: 'create_implementation_branch', description: 'Create implementation branch', parameters: {}, integration_type: 'github' },
          { name: 'commit_implementation_code', description: 'Commit implementation code', parameters: {}, integration_type: 'github' },
          { name: 'create_implementation_pull_request', description: 'Create implementation pull request', parameters: {}, integration_type: 'github' },
        ]
      };
    }
  },

  async searchAgents(workspace_id: string, query: string): Promise<Agent[]> {
    try {
      if (!query.trim()) {
        return [];
      }

      const response = await apiClient.get<AgentListResponse>(`/agents?workspace_id=${workspace_id}&search=${encodeURIComponent(query)}&per_page=50`);
      return response.data.agents;
    } catch (error) {
      console.error('Error searching agents:', error);
      throw error;
    }
  },

  async toggleAgentStatus(id: number, isActive: boolean): Promise<Agent> {
    try {
      const response = await apiClient.put<Agent>(`/agents/${id}`, { is_active: isActive });
      return response.data;
    } catch (error) {
      console.error('Error toggling agent status:', error);
      throw error;
    }
  },
}; 